/**
 * GameResultCard - Visual game prediction card for Knewton chat results
 * Shows matchup, predicted winner, scores, confidence, and analysis
 */

import {
  TrophyIcon,
  ChartBarIcon
} from '@heroicons/react/24/solid';
import ConfidenceRing from '../common/ConfidenceRing';

/**
 * Get gradient based on confidence
 */
const getCardGradient = (confidence) => {
  if (confidence >= 7.5) return 'from-green-900/40 to-emerald-900/20';
  if (confidence >= 5) return 'from-blue-900/40 to-indigo-900/20';
  return 'from-yellow-900/40 to-amber-900/20';
};

/**
 * Get AI provider display name
 */
const getProviderName = (provider) => {
  if (provider === 'mvp_matrix') return 'MVP Matrix';
  if (provider === 'forecast_champion') return 'Forecast Champion';
  return provider || 'AI';
};

export default function GameResultCard({ game }) {
  const {
    sport,
    home_team,
    home_team_abbr,
    home_logo,
    away_team,
    away_team_abbr,
    away_logo,
    game_time,
    predicted_winner,
    home_score,
    away_score,
    total_score,
    confidence,
    analysis,
    spread,
    over_under,
    ai_provider
  } = game;

  const isHomeWinner = predicted_winner === 'HOME';
  const isAwayWinner = predicted_winner === 'AWAY';

  // Format confidence for display (ensure it's 0-10)
  const displayConfidence = Math.min(Math.max(confidence || 0, 0), 10);

  return (
    <div className={`
      relative overflow-hidden rounded-xl border border-gray-700/50
      bg-gradient-to-br ${getCardGradient(displayConfidence)}
      hover:border-purple-500/40 transition-all duration-200
    `}>
      {/* Main content */}
      <div className="p-4">
        {/* Header - Sport badge and AI provider */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 text-[10px] font-bold uppercase border border-purple-500/40">
              {sport}
            </span>
            {game_time && (
              <span className="text-xs text-gray-400">{game_time}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
            <ChartBarIcon className="w-3 h-3" />
            {getProviderName(ai_provider)}
          </div>
        </div>

        {/* Matchup display */}
        <div className="flex items-center justify-between mb-4">
          {/* Away team */}
          <div className={`flex-1 text-center transition-all duration-300 ${
            isAwayWinner ? 'scale-105' : 'opacity-70'
          }`}>
            <div className="relative inline-block">
              {away_logo ? (
                <img
                  src={away_logo}
                  alt={away_team}
                  className="w-14 h-14 object-contain mx-auto mb-2"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center mx-auto mb-2 border-2 border-gray-600">
                  <span className="text-xl font-bold text-gray-300">
                    {away_team_abbr?.charAt(0) || '?'}
                  </span>
                </div>
              )}
              {isAwayWinner && (
                <TrophyIcon className="absolute -top-1 -right-1 w-5 h-5 text-yellow-400" />
              )}
            </div>
            <p className={`font-bold text-sm ${isAwayWinner ? 'text-white' : 'text-gray-400'}`}>
              {away_team_abbr}
            </p>
            {away_score !== null && away_score !== undefined && (
              <p className={`text-2xl font-black ${isAwayWinner ? 'text-green-400' : 'text-gray-500'}`}>
                {Math.round(away_score)}
              </p>
            )}
          </div>

          {/* VS / @ separator */}
          <div className="px-4">
            <span className="text-gray-500 text-lg font-light">@</span>
          </div>

          {/* Home team */}
          <div className={`flex-1 text-center transition-all duration-300 ${
            isHomeWinner ? 'scale-105' : 'opacity-70'
          }`}>
            <div className="relative inline-block">
              {home_logo ? (
                <img
                  src={home_logo}
                  alt={home_team}
                  className="w-14 h-14 object-contain mx-auto mb-2"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center mx-auto mb-2 border-2 border-gray-600">
                  <span className="text-xl font-bold text-gray-300">
                    {home_team_abbr?.charAt(0) || '?'}
                  </span>
                </div>
              )}
              {isHomeWinner && (
                <TrophyIcon className="absolute -top-1 -right-1 w-5 h-5 text-yellow-400" />
              )}
            </div>
            <p className={`font-bold text-sm ${isHomeWinner ? 'text-white' : 'text-gray-400'}`}>
              {home_team_abbr}
            </p>
            {home_score !== null && home_score !== undefined && (
              <p className={`text-2xl font-black ${isHomeWinner ? 'text-green-400' : 'text-gray-500'}`}>
                {Math.round(home_score)}
              </p>
            )}
          </div>
        </div>

        {/* Prediction details */}
        <div className="bg-gray-900/50 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between">
            {/* Predicted winner */}
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">
                Predicted Winner
              </p>
              <p className="text-lg font-bold text-green-400">
                {isHomeWinner ? home_team : away_team}
              </p>
            </div>

            {/* Confidence ring */}
            <ConfidenceRing
              confidence={displayConfidence}
              size="sm"
              showLabel={false}
            />
          </div>

          {/* Betting lines if available */}
          {(spread !== null || over_under !== null) && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-700/50">
              {spread !== null && spread !== undefined && (
                <div className="flex-1">
                  <p className="text-[10px] text-gray-500 uppercase">Spread</p>
                  <p className="text-sm font-semibold text-gray-300">
                    {spread > 0 ? '+' : ''}{spread}
                  </p>
                </div>
              )}
              {over_under !== null && over_under !== undefined && (
                <div className="flex-1">
                  <p className="text-[10px] text-gray-500 uppercase">O/U</p>
                  <p className="text-sm font-semibold text-gray-300">{over_under}</p>
                </div>
              )}
              {total_score !== null && total_score !== undefined && (
                <div className="flex-1">
                  <p className="text-[10px] text-gray-500 uppercase">Pred Total</p>
                  <p className="text-sm font-semibold text-gray-300">{Math.round(total_score)}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Analysis if available */}
        {analysis && (
          <p className="text-xs text-gray-400 italic line-clamp-3 px-1">
            "{analysis}"
          </p>
        )}
      </div>

      {/* Decorative corner gradient */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-purple-500/10 to-transparent pointer-events-none" />
    </div>
  );
}
